import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import {
  useGetDispositionTree,
  useGetDispositionDetail,
  useGetDispositionVersion,
  invalidateDispositionTree,
} from 'actions/chatbot';
import TreeView from './tree-view';
import EditForm from './edit-form';
import CategoryForm from './category-form';
import { DISPOSITION_FILTER, DISPOSITION_FILTER_OPTIONS } from './constants';
import {
  Container,
  LeftPanel,
  RightPanel,
  EmptyState,
  NewButton,
  ListSummary,
  FilterToggle,
  FilterOption,
} from './styled-comps';

// Narrow the disposition tree to a single user_type view. A leaf matches when
// its `user_types` include the filter, or when it is global (no channels →
// empty `user_types`, visible everywhere). A category survives only if at least
// one descendant leaf survives, so empty folders fall away on their own. ALL
// leaves the tree untouched.
const filterTreeByUserType = (nodes, filter) => {
  if (filter === DISPOSITION_FILTER.ALL) return nodes;
  const result = [];
  nodes.forEach(node => {
    if (node.is_leaf) {
      const types = node.user_types || [];
      if (types.length === 0 || types.includes(filter)) result.push(node);
      return;
    }
    const children = filterTreeByUserType(node.children || [], filter);
    if (children.length > 0) result.push({ ...node, children });
  });
  return result;
};

function DispositionsTab({ channels }) {
  const dispatch = useDispatch();
  const [selectedNode, setSelectedNode] = useState(null);
  const [disposition, setDisposition] = useState(null);
  const [category, setCategory] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [userTypeFilter, setUserTypeFilter] = useState(DISPOSITION_FILTER.ALL);

  // channel name → user_type, used to tell whether a just-saved disposition
  // would survive the active filter.
  const channelUserType = useMemo(
    () => Object.fromEntries((channels || []).map(ch => [ch.name, ch.user_type])),
    [channels],
  );

  const {
    get: getTree,
    data: treeData,
    isProcessing: treeLoading,
    error: treeError,
  } = useGetDispositionTree();

  const {
    get: getDispositionDetail,
    data: dispositionDetailData,
    error: detailError,
  } = useGetDispositionDetail();

  const {
    get: getDispositionVersion,
    data: dispositionVersionData,
    error: versionError,
  } = useGetDispositionVersion();

  const tree = useMemo(() => (Array.isArray(treeData) ? treeData : []), [treeData]);

  const fetchTree = useCallback(() => {
    getTree('tree');
  }, [getTree]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  useEffect(() => {
    if (treeError) {
      dispatch(
        showNotification({
          text: 'Failed to fetch disposition tree',
          duration: 5,
          showCloseIcon: true,
        }),
      );
    }
  }, [treeError, dispatch]);

  useEffect(() => {
    if (detailError || versionError) {
      dispatch(
        showNotification({
          text: 'Failed to fetch disposition detail',
          duration: 5,
          showCloseIcon: true,
        }),
      );
    }
  }, [detailError, versionError, dispatch]);

  useEffect(() => {
    if (dispositionDetailData && selectedNode) {
      setDisposition(dispositionDetailData);
    }
  }, [dispositionDetailData, selectedNode]);

  useEffect(() => {
    if (dispositionVersionData) {
      setDisposition(dispositionVersionData);
      setSelectedNode(dispositionVersionData.path);
    }
  }, [dispositionVersionData]);

  const handleCategoryUpdated = () => {
    fetchTree();
  };

  const handleSelect = node => {
    setIsNew(false);
    setSelectedNode(node.path);
    setCategory(node.is_category ? node : null);
    setDisposition(null);

    if (!node.is_category) {
      getDispositionDetail(node.path);
    }
  };

  const handleNew = () => {
    setSelectedNode(null);
    setIsNew(true);
    setCategory(null);
    setDisposition({
      path: '',
      display_name: '',
      bot_instructions: '',
      model: 'sonnet',
      can_ai_resolve: false,
      escalation_team: {},
      kb_scope: '',
      channels: [],
      status: 'DRAFT',
    });
  };

  const handleSaved = (createdPath, savedChannels) => {
    invalidateDispositionTree();
    fetchTree();
    setIsNew(false);
    // A save can create/retag a disposition so it no longer matches the active
    // filter (e.g. a teacher-only item saved while Parent is selected). Without
    // this, the just-saved item stays open in the right panel but is pruned from
    // the tree. Drop back to ALL when the saved item wouldn't survive the filter.
    if (userTypeFilter !== DISPOSITION_FILTER.ALL) {
      const savedTypes = [
        ...new Set(
          (savedChannels || []).map(name => channelUserType[name]).filter(Boolean),
        ),
      ];
      const survives = savedTypes.length === 0 || savedTypes.includes(userTypeFilter);
      if (!survives) setUserTypeFilter(DISPOSITION_FILTER.ALL);
    }
    const pathToSelect = createdPath || selectedNode;
    if (pathToSelect && !category) {
      handleSelect({ path: pathToSelect, is_leaf: true });
    }
  };

  const handleSelectVersion = version => {
    if (version && version.id) {
      getDispositionVersion(version.id);
    }
  };

  const visibleTree = useMemo(() => filterTreeByUserType(tree, userTypeFilter), [
    tree,
    userTypeFilter,
  ]);

  // Count leaves + how many are currently live (PUBLISHED) in the visible tree,
  // so the summary tracks the active filter.
  const counts = (() => {
    let total = 0;
    let live = 0;
    const walk = nodes => {
      nodes.forEach(n => {
        if (n.is_leaf) {
          total += 1;
          if (n.has_published) live += 1;
        }
        if (n.children && n.children.length) walk(n.children);
      });
    };
    walk(visibleTree);
    return { total, live };
  })();

  const emptyFilteredMessage =
    userTypeFilter === DISPOSITION_FILTER.PARENT
      ? 'No parent dispositions'
      : 'No teacher dispositions';

  const renderRightPanel = () => {
    if (category && !isNew) {
      return <CategoryForm category={category} onSaved={handleCategoryUpdated} />;
    }
    if (disposition) {
      return (
        <EditForm
          disposition={disposition}
          channels={channels}
          onSaved={handleSaved}
          onSelectVersion={handleSelectVersion}
          isNew={isNew}
        />
      );
    }
    return <EmptyState>Select a disposition from the tree</EmptyState>;
  };

  return (
    <Container>
      <LeftPanel>
        <NewButton label="+ New Disposition" onClick={handleNew} />
        <FilterToggle>
          {DISPOSITION_FILTER_OPTIONS.map(option => (
            <FilterOption
              key={option.value}
              $active={userTypeFilter === option.value}
              onClick={() => setUserTypeFilter(option.value)}
            >
              {option.label}
            </FilterOption>
          ))}
        </FilterToggle>
        {!treeLoading && counts.total > 0 && (
          <ListSummary>
            {counts.total} disposition{counts.total === 1 ? '' : 's'} · {counts.live} live
          </ListSummary>
        )}
        {treeLoading ? (
          <EmptyState>Loading...</EmptyState>
        ) : visibleTree.length === 0 && userTypeFilter !== DISPOSITION_FILTER.ALL ? (
          <EmptyState>{emptyFilteredMessage}</EmptyState>
        ) : (
          <TreeView
            tree={visibleTree}
            selectedPath={selectedNode}
            onSelect={handleSelect}
            onCategoryRenamed={handleCategoryUpdated}
          />
        )}
      </LeftPanel>
      <RightPanel>{renderRightPanel()}</RightPanel>
    </Container>
  );
}

export default DispositionsTab;
